/**
 * Centralized configuration for the recommendation system.
 *
 * Adding a new signal type or prepared action type means adding one entry
 * here — UI rendering, action execution, and label formatting all derive
 * from these registries.
 */

import type { LucideIcon } from 'lucide-react'
import { Mail, Snowflake, ArrowRight } from 'lucide-react'
import type {
  SignalType,
  EnrichedAction,
  PreparedAction,
} from '../../services/recommendations_contracts'
import { applyPreparedAction } from '../../services/recommendations_api'

// ---------------------------------------------------------------------------
// Signal Type Configuration
// ---------------------------------------------------------------------------

export interface SignalTypeConfig {
  /** Display title for section headers (plural, e.g. "Unreplied Emails") */
  title: string
  /** Singular form for individual card labels (e.g. "Unreplied Email") */
  singularTitle: string
  /** Description shown in section tooltips */
  description: string
  /** Tooltip content (may differ from description) */
  tooltip: string
  /** Lucide icon for the signal type */
  icon: LucideIcon
  /** Link target when clicking "View" on a card */
  linkTarget: 'deal' | 'company'
  /** Format the days-based label for this signal */
  formatLabel: (action: EnrichedAction) => string
}

// Insertion order determines the Signal view tab order in the UI.
// Do not reorder entries without updating the tab layout.
export const SIGNAL_CONFIG: Record<SignalType, SignalTypeConfig> = {
  unreplied_email: {
    title: 'Unreplied Emails',
    singularTitle: 'Unreplied Email',
    description: 'Inbound messages from 3-60 days ago awaiting your response',
    tooltip:
      'Inbound emails from 3-60 days ago awaiting your response. Automated emails (shipping, receipts, etc.) are filtered out.',
    icon: Mail,
    linkTarget: 'company',
    formatLabel: (a) => `${a.days_unreplied ?? 0}d unreplied`,
  },
  inactive_deal: {
    title: 'Inactive Deals',
    singularTitle: 'Inactive Deal',
    description:
      'Deals needing attention (7-90 days stale or 14+ days in same stage)',
    tooltip:
      'Deals with no activity in 7+ days OR stuck in the same stage for 14+ days. Limited to deals active within the last 90 days.',
    icon: Snowflake,
    linkTarget: 'company',
    formatLabel: (a) => {
      if (a.days_in_stage && a.days_in_stage >= 14) {
        return `${a.days_in_stage}d in ${a.current_stage || 'stage'}`
      }
      return `${a.days_stale ?? 0}d inactive`
    },
  },
  email_stage_change: {
    title: 'Update Deal Stage',
    singularTitle: 'Update Deal Stage',
    description:
      'Deals where recent inbound emails suggest a stage advancement is warranted',
    tooltip:
      'A recent inbound email suggests this deal may be ready to advance to the next stage. Review and apply the AI-recommended stage change.',
    icon: ArrowRight,
    linkTarget: 'deal',
    formatLabel: (a) =>
      `${a.days_in_stage ?? 0}d in ${a.current_stage || 'stage'}`,
  },
}

// ---------------------------------------------------------------------------
// Prepared Action Executors
// ---------------------------------------------------------------------------

/** API surface needed by action executors (subset of useApiService return). */
export interface ActionApi {
  createDealNote: (
    dealId: number,
    content: string,
    attachments?: File[]
  ) => Promise<unknown>
}

export type ActionExecutor = (
  dealId: number,
  pa: PreparedAction,
  api: ActionApi
) => Promise<void>

export const ACTION_EXECUTORS: Record<string, ActionExecutor> = {
  update_stage: async (dealId, pa) => {
    if (pa.type !== 'update_stage') return
    await applyPreparedAction(dealId, pa.target_stage_id)
  },
  add_note: async (dealId, pa, api) => {
    if (pa.type !== 'add_note') return
    await api.createDealNote(dealId, pa.draft_content, pa.attachments)
  },
}

/**
 * Execute a single prepared action by dispatching to the appropriate executor.
 *
 * @throws Re-throws any API errors (callers handle toast + bail-out).
 */
export async function executePreparedAction(
  dealId: number,
  pa: PreparedAction,
  api: ActionApi
): Promise<void> {
  const executor = ACTION_EXECUTORS[pa.type]
  if (!executor) {
    console.warn(`No executor registered for prepared action type: ${pa.type}`)
    return
  }
  await executor(dealId, pa, api)
}
